import { useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  useFuncionarios, useAdicionaisDaTarefa, useCriarAdicionais, useExcluirAdicional,
  useQuinzenas, useAdicionaisPorCompetencia,
} from "@/hooks/use-equipe";
import {
  formatarBRL, calcValorPorKg, paraCentavos, rotuloTipoAdicional,
  competenciaDaQuinzena, calcAvisoTetoAdicional, formatarAvisoTetoAdicional,
} from "@shared/equipe-fechamento";

interface Props {
  tarefaId: string;
  /** Pré-marcado por default — é normal o adicional ser do mesmo funcionário da tarefa. */
  funcionarioIdDaTarefa: string;
  /** Data da tarefa (YYYY-MM-DD) — usada só para achar a competência do aviso de teto (§3). */
  data: string;
}

/** Mensagem do servidor a partir do erro do apiRequest ("409: {json}"). */
function msgDoErro(e: unknown, fallback: string): string {
  const bruto = String((e as any)?.message ?? "");
  try {
    const j = JSON.parse(bruto.slice(bruto.indexOf(":") + 1).trim());
    if (typeof j?.error === "string") return j.error;
  } catch {
    // Corpo não-JSON: cai no fallback. Nunca engolir o erro em silêncio.
  }
  return fallback;
}

interface LinhaRateio {
  funcionarioId: string;
  marcado: boolean;
  kg: string;
  valorKg: string;
  valor: string; // usado no caminho OUTRO
}

/**
 * §5.1 — bloco "Pagamento adicional" na tela da tarefa.
 *
 * ⚠️ NÃO é diária. É a UI de `tarefa_adicional` (Fase 3): um valor extra por
 * funcionário, pago na quinzena, somado só no total — nunca dentro do valor da
 * diária/registro. Recolhido por default (a maioria das tarefas não tem).
 *
 * O R$/kg vem sugerido de `funcionarios.valorKgColheita` (cadastro), mas é
 * EDITÁVEL aqui antes de salvar — o servidor aceita o override e congela o que
 * for enviado como snapshot (nunca recalcula depois).
 */
export function PagamentoAdicionalBlock({ tarefaId, funcionarioIdDaTarefa, data: dataTarefa }: Props) {
  const [aberto, setAberto] = useState(false);
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: existentes = [], isLoading } = useAdicionaisDaTarefa(tarefaId);
  const criar = useCriarAdicionais();
  const excluir = useExcluirAdicional();
  const { toast } = useToast();

  // §3 — aviso de teto: acha a quinzena que cobre a data da tarefa, deriva a
  // competência dela (D11 — nunca o mês calendário da data isolada) e busca o
  // que a pessoa já tem lançado no mês. Só para exibição, nunca bloqueia.
  const { data: quinzenas = [] } = useQuinzenas();
  const quinzenaDaTarefa = quinzenas.find(q => dataTarefa >= q.periodoInicio && dataTarefa <= q.periodoFim);
  const competencia = quinzenaDaTarefa ? competenciaDaQuinzena(quinzenaDaTarefa) : undefined;
  const { data: adicionaisComp = [] } = useAdicionaisPorCompetencia(competencia);
  const jaLancadoNoMes = new Map(adicionaisComp.map(a => [a.funcionario_id, paraCentavos(a.total)]));

  const [tipo, setTipo] = useState<"COLHEITA" | "OUTRO">("COLHEITA");
  const [descricao, setDescricao] = useState("");
  const [kgTotal, setKgTotal] = useState("");
  const [linhas, setLinhas] = useState<LinhaRateio[]>([]);

  const funcionariosAtivos = funcionarios.filter(f => f.ativo);

  const totalExistente = existentes.reduce((acc, a) => acc + paraCentavos(a.valor), 0);

  /** Reabre a lista de rateio com todo mundo desmarcado, exceto quem já vem pré-selecionado. */
  const abrirFormulario = () => {
    setLinhas(
      funcionariosAtivos.map(f => ({
        funcionarioId: f.id,
        marcado: f.id === funcionarioIdDaTarefa,
        kg: "",
        valorKg: f.valorKgColheita ?? "",
        valor: "",
      })),
    );
    setKgTotal("");
    setDescricao("");
  };

  const toggleMarcado = (funcionarioId: string) => {
    setLinhas(ls => ls.map(l => l.funcionarioId === funcionarioId ? { ...l, marcado: !l.marcado } : l));
  };

  /** Divide o kg total igualmente entre os marcados — editável depois, por pessoa. */
  const dividirIgual = () => {
    const marcados = linhas.filter(l => l.marcado);
    const total = Number(kgTotal.replace(",", "."));
    if (!marcados.length || !Number.isFinite(total) || total <= 0) return;
    const porPessoa = (total / marcados.length).toFixed(2);
    setLinhas(ls => ls.map(l => l.marcado ? { ...l, kg: porPessoa } : l));
  };

  const atualizarLinha = (funcionarioId: string, patch: Partial<LinhaRateio>) => {
    setLinhas(ls => ls.map(l => l.funcionarioId === funcionarioId ? { ...l, ...patch } : l));
  };

  const marcados = linhas.filter(l => l.marcado);
  const podeSalvar = tipo === "COLHEITA"
    ? marcados.length > 0 && marcados.every(l => Number(l.kg) > 0 && Number(l.valorKg) > 0)
    : marcados.length > 0 && marcados.every(l => Number(l.valor) > 0);

  const previaValor = (l: LinhaRateio) =>
    tipo === "COLHEITA" ? calcValorPorKg(l.kg, l.valorKg) : (l.valor || "0.00");

  const totalPrevia = marcados.reduce((acc, l) => acc + paraCentavos(previaValor(l)), 0);

  const salvar = async () => {
    try {
      await criar.mutateAsync({
        tarefaId,
        tipo,
        descricao: descricao.trim() || undefined,
        itens: marcados.map(l => tipo === "COLHEITA"
          ? { funcionarioId: l.funcionarioId, kg: l.kg, valorKg: l.valorKg }
          : { funcionarioId: l.funcionarioId, valor: l.valor }),
      });
      toast({ title: "Adicional lançado!" });
      setLinhas([]);
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao lançar adicional"), variant: "destructive" });
    }
  };

  const apagar = async (id: string) => {
    try {
      await excluir.mutateAsync(id);
      toast({ title: "Adicional removido." });
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao remover adicional"), variant: "destructive" });
    }
  };

  return (
    <div className="rounded-lg border overflow-hidden">
      <button
        type="button"
        onClick={() => { const next = !aberto; setAberto(next); if (next && linhas.length === 0) abrirFormulario(); }}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-muted hover:bg-muted transition-colors"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Sprout className="h-4 w-4 text-sucesso" />
          Pagamento adicional
          {existentes.length > 0 && (
            <Badge variant="outline" className="ml-1 bg-sucesso-suave text-sucesso border-sucesso/30 text-[10px]">
              {existentes.length} · {formatarBRL(totalExistente / 100)}
            </Badge>
          )}
        </span>
        {aberto ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {aberto && (
        <div className="p-3 space-y-3 border-t">
          {/* Lançamentos existentes */}
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Carregando...</p>
          ) : existentes.length > 0 && (
            <div className="space-y-1.5">
              {existentes.map(a => {
                const func = funcionarios.filter(f => f.id === a.funcionarioId)[0];
                return (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg bg-sucesso-suave border border-sucesso/30 px-2.5 py-1.5 text-xs">
                    <div className="min-w-0">
                      <span className="font-medium text-sucesso">{func?.apelido ?? func?.nome ?? a.funcionarioId}</span>
                      <span className="text-sucesso ml-1.5">
                        {a.kg ? `${a.kg} kg × ${formatarBRL(a.valorKg)}` : (a.descricao || "adicional")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold text-sucesso">+ {formatarBRL(a.valor)}</span>
                      <button
                        type="button"
                        onClick={() => apagar(a.id)}
                        disabled={excluir.isPending}
                        title="Remover"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-sucesso hover:text-erro" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Novo lançamento */}
          <div className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              {(["COLHEITA", "OUTRO"] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipo(t)}
                  className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    tipo === t ? "bg-sucesso-suave text-sucesso border-sucesso/30" : "bg-card text-muted-foreground border-border"
                  }`}
                >
                  {rotuloTipoAdicional(t)}
                </button>
              ))}
            </div>

            {tipo === "COLHEITA" && (
              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">kg total colhido</label>
                <div className="flex gap-2">
                  <input
                    type="number" inputMode="decimal" step="0.01"
                    value={kgTotal}
                    onChange={e => setKgTotal(e.target.value)}
                    placeholder="0,00"
                    className="flex-1 min-w-0 rounded-lg border border-border px-2.5 py-1.5 text-sm"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={dividirIgual}>
                    Dividir igual
                  </Button>
                </div>
              </div>
            )}

            {tipo === "OUTRO" && (
              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">Descrição</label>
                <input
                  value={descricao}
                  onChange={e => setDescricao(e.target.value)}
                  placeholder="ex.: diária extra de manutenção"
                  className="w-full rounded-lg border border-border px-2.5 py-1.5 text-sm"
                />
              </div>
            )}

            {/* Lista de quem recebe */}
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {linhas.map(l => {
                const func = funcionariosAtivos.filter(f => f.id === l.funcionarioId)[0];
                if (!func) return null;
                const avisoTeto = l.marcado
                  ? calcAvisoTetoAdicional({
                      jaLancadoCentavos: jaLancadoNoMes.get(l.funcionarioId) ?? 0,
                      novoCentavos: paraCentavos(previaValor(l)),
                      variavelMaxMensal: func.variavelMaxMensal,
                    })
                  : null;
                return (
                  <div key={l.funcionarioId}>
                    <div className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={l.marcado}
                      onChange={() => toggleMarcado(l.funcionarioId)}
                      className="shrink-0"
                    />
                    <span className="flex-1 min-w-0 truncate text-foreground">{func.apelido ?? func.nome}</span>
                    {l.marcado && tipo === "COLHEITA" && (
                      <>
                        <input
                          type="number" inputMode="decimal" step="0.01"
                          value={l.kg}
                          onChange={e => atualizarLinha(l.funcionarioId, { kg: e.target.value })}
                          placeholder="kg"
                          className="w-16 rounded-lg border border-border px-1.5 py-1 text-xs text-right"
                        />
                        <input
                          type="number" inputMode="decimal" step="0.0001"
                          value={l.valorKg}
                          onChange={e => atualizarLinha(l.funcionarioId, { valorKg: e.target.value })}
                          title="R$/kg — sugerido do cadastro, editável"
                          className="w-16 rounded-lg border border-border px-1.5 py-1 text-xs text-right"
                        />
                        <span className="w-16 shrink-0 text-right text-xs font-medium text-sucesso">
                          {formatarBRL(previaValor(l))}
                        </span>
                      </>
                    )}
                    {l.marcado && tipo === "OUTRO" && (
                      <input
                        type="number" inputMode="decimal" step="0.01"
                        value={l.valor}
                        onChange={e => atualizarLinha(l.funcionarioId, { valor: e.target.value })}
                        placeholder="R$"
                        className="w-24 rounded-lg border border-border px-1.5 py-1 text-xs text-right"
                      />
                    )}
                  </div>
                  {avisoTeto?.estourou && (
                    <p className="text-[11px] text-atencao pl-6">
                      {func.apelido ?? func.nome}: {formatarAvisoTetoAdicional(avisoTeto)}
                    </p>
                  )}
                  </div>
                );
              })}
            </div>

            {marcados.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {marcados.length} pessoa{marcados.length === 1 ? "" : "s"} · total {formatarBRL(totalPrevia / 100)}
              </p>
            )}

            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={!podeSalvar || criar.isPending}
              onClick={salvar}
            >
              {criar.isPending ? "Salvando..." : "Lançar adicional"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
